import React, { FunctionComponent, ReactNode, useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import DropdownMenu from '../../components/dropdown/DropdownMenu'
import DropdownItem from '../../components/dropdown/DropdownItem'
import Row from '../../components/Row'
import FilterDropdownButton from './FilterDropdownButton'
import Text from '../../components/Text'
import Column from '../../components/Column'
import FormControl from '../../components/FormControl'
import InputNumber from '../../components/InputNumber'
import Button from '../../components/Button'

interface IMinMaxRangeDropdownProps {
    prefilledMinValue?: number,
    prefilledMaxValue?: number,
    className?: string
    title?: string
    description?: ReactNode,
    icon?: ReactNode,
    svgType?: string,
    resetFilter: () => void,
    onChangeHandler: (values: { minValue?: number, maxValue?: number}) => void
}

const MinMaxRangeDropdown: FunctionComponent<IMinMaxRangeDropdownProps> = ({ className, title, description, icon, svgType = 'profileAdd', resetFilter, onChangeHandler, prefilledMinValue, prefilledMaxValue }) => {

    const [isOpen, setIsOpen] = useState(false)
    const [ minValue, setMinValue ] = useState<number | undefined>(undefined)
    const [ maxValue, setMaxValue ] = useState<number | undefined>(undefined)

    useEffect(() => {
        setMinValue(prefilledMinValue ?? undefined)
        setMaxValue(prefilledMaxValue ?? undefined)

        // If the parent clears values (e.g. "Clear all"), close the dropdown too.
        if (prefilledMinValue === undefined && prefilledMaxValue === undefined) {
            setIsOpen(false)
        }
    }, [prefilledMinValue, prefilledMaxValue])

    const resetFilterHandler = () => {
        setMinValue(undefined)
        setMaxValue(undefined)
        resetFilter()
    }

    const submitFormHandler = useCallback(() => {
        onChangeHandler({ minValue, maxValue})
        setIsOpen(false)
    }, [minValue, maxValue, onChangeHandler])

    const hasValue = minValue !== undefined || maxValue !== undefined
    const dropdownButton = <FilterDropdownButton 
            icon={icon}
            svgType={icon ? undefined : svgType}
            label={title}
            active={hasValue}
            value={hasValue ? <>
                    {minValue ?? 'Min'}
                    {' - '}
                    {maxValue ?? 'Max'}
            </> : 'Any'}
            resetFilter={hasValue ? () => resetFilterHandler() : undefined}
        />

    return <StyledDropdownMenu
        className={className}
        title={dropdownButton}
        autoClose='outside'
        selectStyle
        $hideToggle={minValue !== undefined || maxValue !== undefined}
        show={isOpen}
        onToggle={(nextIsOpen) => setIsOpen(nextIsOpen)}
    >
        <DropdownItem $nonHoverable>
            <Column gap="10px">
                <Text $heading5>{title}</Text>
                {description && <Text>{description}</Text>}
                <Row spaceBetween>
                    <FormControl>
                        <Text $label>Min</Text>
                        <InputNumber prefilledValue={minValue} onChangeHandler={(number: number) => setMinValue(maxValue && number > maxValue ? maxValue : number)}/>
                    </FormControl>
                    <FormControl>
                        <Text $label>Max</Text>
                        <InputNumber prefilledValue={maxValue} onChangeHandler={(number: number) => setMaxValue(minValue && number < minValue ? minValue : number)}/>
                    </FormControl>
                </Row>
                <FooterButton>
                    <Button $mediumButton $type="blue" onClickHandler={(e: any) => submitFormHandler()}>{'Search'}</Button>
                </FooterButton>
            </Column>
        </DropdownItem>
    </StyledDropdownMenu>
}

const FooterButton = styled(Row)`
    justify-content: flex-end;
`

const StyledDropdownMenu = styled(DropdownMenu)`
    .actions-dropdown + div{
        width: 100%;
    }
`
export default MinMaxRangeDropdown
